/**
 * Tournament constants — single source of truth for blind levels and tournament config.
 */

/**
 * Big blind values for tournament progression. SB is always BB / 2.
 *
 * Late levels deliberately accelerate (16k → 18k → 20k → 24k → 28k → 35k)
 * so heads-up pairs can't grind indefinitely on starting stacks of 10k.
 * Once BB exceeds the median stack the next M-rotation forces all-ins.
 */
export const BLIND_LEVELS = [
  200, 300, 400, 500, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000,
  11000, 12000, 14000, 16000, 18000, 20000, 24000, 28000, 35000
] as const

/**
 * Tournament create-flow guardrail: the selected starting big blind must be no
 * larger than `startingChips / ratio`.
 */
export const TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO = 4

export const TOURNAMENT_STATE = {
  REGISTRATION: 'registration',
  ACTIVE: 'active',
  FINISHED: 'finished',
  /** Host-initiated cancellation (only allowed while in registration). */
  CANCELLED: 'cancelled',
  /** Lifecycle cron force-closed a stale tournament that never started. */
  EXPIRED: 'expired'
} as const

export const TOURNAMENT_STATES_FOR_USER_LIST = [
  TOURNAMENT_STATE.REGISTRATION,
  TOURNAMENT_STATE.ACTIVE,
  TOURNAMENT_STATE.FINISHED
] as const

export const TOURNAMENT_SYSTEM_USER_NAME = 'System'
export const TOURNAMENT_BOT_USER_ID_PREFIX = 'bot-'
export const TOURNAMENT_PRESENCE_ONLINE_WINDOW_MS = 90_000

/**
 * Minimum interval between `pingTournamentPresence` writes for the same
 * (tournament, user). The lobby ping cadence is generous; back-to-back
 * pings within this window are a no-op so we don't burn writes on a tight
 * polling loop. Should be well under `TOURNAMENT_PRESENCE_ONLINE_WINDOW_MS`
 * so a presence row stays fresh between throttled writes.
 */
export const TOURNAMENT_PRESENCE_PING_MIN_INTERVAL_MS = 5_000
export const TOURNAMENT_REBALANCE_COOLDOWN_MS = 3 * 60 * 1000
/**
 * Delay between staging a `pendingTableMove` on idle source tables and
 * actually applying the move. This window exists so the client can render
 * the "Moving to Table N after this hand" banner/toast — without it, the
 * stage-and-apply happens in the same Convex transaction and the client
 * never observes `pendingTableMove === truthy`.
 *
 * Tuning trade-offs:
 *  - Too short: banner flashes too fast to read (sub-second)
 *  - Too long: feels like a stall between hands when players are watching
 *  - 5 seconds: comfortable read window, well under typical hand duration
 *
 * Only affects moves staged on IDLE source tables (between hands). Mid-hand
 * source tables run their own orchestratePostHand at hand_complete and
 * apply staged moves naturally — they already provide a banner window
 * equal to the remaining hand duration.
 */
export const TOURNAMENT_MOVE_BANNER_DELAY_MS = 5_000
export const TOURNAMENT_STALE_CLOSE_WINDOW_MS = 30 * 60 * 1000
/**
 * Hard upper bound on tournament registrations. The `tournaments` document embeds
 * the registrations array, and Convex caps documents at 1MB. Each registration row
 * is roughly ~150–250 bytes, so 500 leaves comfortable headroom for other fields and
 * runtime growth (cohostUserIds, scheduled job IDs, etc).
 */
export const TOURNAMENT_MAX_REGISTRATIONS = 500

/**
 * Minimum allowed `gracePeriodMs` for tournament configs. Prevents:
 *  1. Auto-decline scheduled at `runAfter(0)` (forbidden anti-pattern)
 *  2. UX bug where the reentry modal pops and is auto-dismissed on the same tick
 *  3. Sit-out grace eliminations firing before a player can rejoin
 * 30 seconds is a safe floor for both purposes.
 */
export const TOURNAMENT_MIN_GRACE_PERIOD_MS = 30_000

/**
 * Hard cap on co-hosts per tournament. Keeps the embedded `cohostUserIds` array
 * from bloating the tournament document and discourages a host from accidentally
 * (or maliciously) granting host-level powers to a large group.
 */
export const TOURNAMENT_MAX_COHOSTS = 10

/** Max characters per chat message; rejected by `sendTournamentMessage`. */
export const TOURNAMENT_MAX_MESSAGE_LENGTH = 500

/**
 * Minimum interval between two messages from the same user in the same
 * tournament. A repeat send-attempt inside this window is rejected by
 * `sendTournamentMessage`. Anti-flood for the lobby chat.
 *
 * 1 second is comfortable for human typing cadence and tight enough to
 * blunt a scripted attacker. The frontend can also debounce client-side,
 * but the server contract has to be the source of truth.
 */
export const TOURNAMENT_MESSAGE_MIN_INTERVAL_MS = 1000

/**
 * Retention window for `tournamentMessages`. The lobby prune cron drops
 * rows older than this so the table doesn't grow without bound. 30 days
 * is generous for postmortem inspection and well past the typical
 * tournament playback window.
 */
export const TOURNAMENT_MESSAGE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Interval for checking tournament countdown toast timers in the frontend.
 * Used by the tournament countdown toast hook to poll for upcoming tournament
 * start times and display warning notifications at 5-minute and 1-minute marks.
 */
export const TOURNAMENT_TOAST_TIMER = 15_000

/**
 * How long a host gets to respond to the "continue with bots / end now" modal
 * before the tournament auto-ends. Without this, a disconnected or distracted host
 * would leave the tournament stuck in `active` forever (modal pending, branch #2a
 * short-circuiting every post-hand cycle). Auto-end is the safer default than
 * auto-continue because finalizing produces a real winner snapshot.
 */
export const TOURNAMENT_HOST_DECISION_TIMEOUT_MS = 10 * 60 * 1000

/**
 * How long a busted player gets to answer the re-entry modal before it
 * auto-declines. Deliberately decoupled from `config.gracePeriodMs` (which
 * governs late-join and sit-out sweeps): a re-entry decision is a quick
 * in-the-moment choice, and a long window lets an unanswered modal hold the
 * whole tournament — including a would-be winner — hostage. Not exposed in
 * the create UI.
 */
export const TOURNAMENT_REENTRY_AUTO_DECLINE_MS = 3 * 60 * 1000

/**
 * Re-entry cap for a tournament config. `maxReentries` is optional for
 * backward compatibility with tournaments created before the rename, so every
 * reader must resolve it through this helper: fall back to the legacy
 * `maxRebuys` field (same semantics: bust-and-come-back), then to 0.
 */
export function getTournamentMaxReentries(config: {
  maxReentries?: number
  maxRebuys?: number
}): number {
  return config.maxReentries ?? config.maxRebuys ?? 0
}

/** Get the big blind value at the given index. Clamps to array bounds. */
export function getBlindLevelByIndex(index: number): number {
  if (index < 0) return BLIND_LEVELS[0]
  if (index >= BLIND_LEVELS.length) return BLIND_LEVELS[BLIND_LEVELS.length - 1]
  return BLIND_LEVELS[index]
}

/**
 * Resolve a blind value to the configured blind schedule. Exact matches keep
 * their index; non-matches floor to the highest level that does not exceed the
 * given blind so tournaments can keep their original starting blind even if the
 * shared blind table changes later.
 */
export function getBlindLevelIndexForBigBlind(bigBlind: number): number {
  if (!Number.isFinite(bigBlind)) return 0

  let index = 0
  for (let i = 0; i < BLIND_LEVELS.length; i++) {
    if (BLIND_LEVELS[i] > bigBlind) {
      return Math.max(0, i - 1)
    }
    index = i
  }

  return index
}

export function getAllowedStartingBlindLevels(startingChips: number) {
  const maxBigBlind = Math.floor(startingChips / TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO)
  return BLIND_LEVELS.flatMap((bigBlind, index) =>
    bigBlind <= maxBigBlind
      ? [{ index, bigBlind, smallBlind: Math.floor(bigBlind / 2) }]
      : []
  )
}

export function isStartingBlindLevelAllowed(startingChips: number, index: number): boolean {
  if (!Number.isInteger(index) || index < 0 || index >= BLIND_LEVELS.length) return false
  const bigBlind = getBlindLevelByIndex(index)
  return bigBlind <= Math.floor(startingChips / TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO)
}

export function isStartingBigBlindAllowed(startingChips: number, bigBlind: number): boolean {
  if (!Number.isInteger(bigBlind) || bigBlind <= 0) return false
  return bigBlind <= Math.floor(startingChips / TOURNAMENT_STARTING_CHIPS_TO_BIG_BLIND_RATIO)
}

/** Get the next blind level after the given index. Returns null if already at max. */
export function getNextBlindLevel(index: number): { bigBlind: number; smallBlind: number; index: number } | null {
  const nextIndex = index + 1
  if (nextIndex >= BLIND_LEVELS.length) return null
  const bb = BLIND_LEVELS[nextIndex]
  return { bigBlind: bb, smallBlind: Math.floor(bb / 2), index: nextIndex }
}

/** Tournament default config values */
export const TOURNAMENT_DEFAULTS = {
  STARTING_CHIPS: 10_000,
  BLIND_INCREASE_INTERVAL_MS: 180_000, // 3 minutes
  MAX_PLAYERS_PER_TABLE: 10,
  GRACE_PERIOD_MS: 900_000, // 15 minutes
  MAX_REENTRIES: 0
} as const

export function getTournamentGracePeriodEndsAt(tournament: {
  scheduledStartTime: number
  config: { gracePeriodMs: number }
}): number {
  return tournament.scheduledStartTime + tournament.config.gracePeriodMs
}
